import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { SectionHeading } from "@/components/Produzioni";
import { STATS } from "@/data/content";
import { useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

function Counter({ to, suffix, start }) {
    const ref = useRef(null);
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!start) return;
        const controls = animate(0, to, {
            duration: 1.8,
            ease: EASE,
            onUpdate: (v) => setVal(Math.round(v)),
            onComplete: () => setVal(to),
        });
        const safety = setTimeout(() => setVal(to), 2400);
        return () => {
            controls.stop();
            clearTimeout(safety);
        };
    }, [start, to]);

    return (
        <span ref={ref} className="font-display text-5xl font-extrabold text-white sm:text-6xl">
            {val}
            <span className="text-brand">{suffix}</span>
        </span>
    );
}

export default function ChiSiamo() {
    const { lang, ui } = useLang();
    const statsRef = useRef(null);
    const statsInView = useInView(statsRef, { once: true, margin: "0px 0px -5% 0px" });

    return (
        <section id="chi-siamo" data-testid="chi-siamo-section" className="relative border-t border-white/5 bg-[#0a0a0d] px-5 py-24 sm:px-8 sm:py-32">
            <div className="mx-auto max-w-7xl">
                <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
                    <div>
                        <SectionHeading
                            label={ui.chisiamo.label}
                            title={ui.chisiamo.title}
                            testid="chi-siamo-heading"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                            className="-mt-8 space-y-5 text-sm leading-relaxed text-slate-400 sm:text-base"
                        >
                            <p>{ui.chisiamo.p1}</p>
                            <p>{ui.chisiamo.p2}</p>
                        </motion.div>

                        <div className="mt-10 space-y-6">
                            {ui.chisiamo.valori.map((v, i) => (
                                <motion.div
                                    key={v.titolo}
                                    initial={{ opacity: 0, x: -28 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
                                    className="flex gap-4"
                                >
                                    <span className="mt-2 h-2 w-2 shrink-0 rotate-45 bg-brand" />
                                    <div>
                                        <h3 className="font-display text-lg font-semibold text-white">{v.titolo}</h3>
                                        <p className="mt-1 text-sm text-slate-400">{v.testo}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1.1, ease: EASE }}
                        className="relative"
                    >
                        <div className="absolute -inset-3 rounded-3xl border border-brand/25" aria-hidden="true" />
                        <div className="relative overflow-hidden rounded-3xl">
                            <img
                                src="https://images.pexels.com/photos/8089650/pexels-photo-8089650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Set cinematografico di una produzione RED PRIVATE"
                                loading="lazy"
                                className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                            <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-300">
                                {ui.chisiamo.caption}
                            </p>
                        </div>
                    </motion.div>
                </div>

                <div ref={statsRef} className="mt-24 grid grid-cols-2 gap-10 border-t border-white/10 pt-14 lg:grid-cols-4">
                    {STATS.map((s) => (
                        <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <Counter to={s.value} suffix={s.suffix} start={statsInView} />
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 sm:text-[11px]">
                                {lang === "en" ? s.label_en : s.label}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
